import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap/';
import { File, Database, Globe, Cloud, FileText, Code, Library, Brain } from 'lucide-react';
import { groupBy, values } from 'lodash';

interface Resource {
    id: string;
    name: string;
    type: string;
    item_type: string;
    path?: string;
    metadata?: {
        original_metadata?: {
            mime_type?: string;
            mimeType?: string;
            file_size?: number;
        };
        display_path?: string;
        full_path?: string;
        file_count?: number;
        folder_prefix?: string;
        path_tokens?: string[];
        folder_name?: string;
        original_folder_prefix?: string;
    };
    folder_id?: string;
    created_at?: string;
    created_by_name?: string;
    description?: string;
    file_path?: string;
    status?: string;
    source_type?: string;
    downloadUrl?: string;
}

interface Index extends Resource {
    dataSourceIds: string[];
}

interface RAGApplication extends Resource {
    indexIds: string[];
}

interface ResourceData {
    dataSources: Resource[];
    indexes: Index[];
    ragApplications: RAGApplication[];
}

interface Position {
    x: number;
    y: number;
}

interface Arrow {
    start: Position;
    end: Position;
    id: string;
    color: string;
    isHighlighted?: boolean;
}

const CONNECTION_COLORS = [
    '#3B82F6', // blue
    '#10B981', // green
    '#8B5CF6', // purple
    '#F59E0B'  // yellow
];

const getSourceTypeIcon = (sourceType?: string) => {
    switch (sourceType) {
        case 'file_upload':
            return <File size={16} className="text-primary" />;
        case 'web_source':
            return <Globe size={16} className="text-success" />;
        case 'api_source':
            return <Code size={16} className="text-purple" />;
        case 'drive_sync':
            return <Cloud size={16} className="text-warning" />;
        default:
            return <File size={16} className="text-secondary" />;
    }
};

const ResourceRelationshipDiagram: React.FC<{ data: ResourceData }> = ({ data }) => {
    const [arrows, setArrows] = useState<Arrow[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());
    const containerRef = useRef<HTMLDivElement>(null);
    const docsRef = useRef<HTMLDivElement>(null);
    const indexesRef = useRef<HTMLDivElement>(null);
    const ragsRef = useRef<HTMLDivElement>(null);

    const filteredDataSources = useMemo(() => {
        const deduplicatedSources = values(groupBy(data.dataSources, 'id')).map(group => {
            const mergedItem = group.reduce((acc, item) => ({
                ...acc,
                ...item,
                source_type: item.source_type || acc.source_type,
                item_type: item.item_type || acc.item_type
            }));
            return mergedItem;
        });

        const filtered = deduplicatedSources.filter(item => {
            const isFolder = item.item_type === 'folder' ||
                item.type === 'folder' ||
                (item.metadata?.file_count !== undefined && !item.source_type);

            if (isFolder) {
                return false;
            }

            const sourceType = item.source_type || (item.item_type === 'file' ? 'file_upload' : null);
            const isValidSourceType = sourceType === 'file_upload' ||
                sourceType === 'web_source' ||
                sourceType === 'api_source' ||
                sourceType === 'drive_sync';

            return isValidSourceType;
        });

        return filtered.sort((a, b) => {
            const sourceTypeA = a.source_type || (a.item_type === 'file' ? 'file_upload' : '');
            const sourceTypeB = b.source_type || (b.item_type === 'file' ? 'file_upload' : '');

            const sourceTypeOrder = {
                'file_upload': 1,
                'web_source': 2,
                'api_source': 3,
                'drive_sync': 4
            };

            const orderA = sourceTypeOrder[sourceTypeA as keyof typeof sourceTypeOrder] || 99;
            const orderB = sourceTypeOrder[sourceTypeB as keyof typeof sourceTypeOrder] || 99;

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
        });
    }, [data.dataSources]);

    const getKnowledgeBaseTypeIcon = (index: Index) => {
        const hasFiles = index.dataSourceIds.some(id =>
            data.dataSources.find(ds => ds.id === id &&
                (ds.source_type === 'file_upload' || ds.source_type === 'drive_sync'))
        );
        const hasWebsites = index.dataSourceIds.some(id =>
            data.dataSources.find(ds => ds.id === id && ds.source_type === 'web_source')
        );
        const hasAPIs = index.dataSourceIds.some(id =>
            data.dataSources.find(ds => ds.id === id && ds.source_type === 'api_source')
        );

        if (hasFiles) {
            return <FileText size={16} className="text-primary" />;
        } else if (hasWebsites) {
            return <Globe size={16} className="text-success" />;
        } else if (hasAPIs) {
            return <Code size={16} className="text-purple" />;
        }

        return <Database size={16} className="text-secondary" />;
    };

    const handleOutsideClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.resource-item')) {
            setSelectedId(null);
            setConnectedIds(new Set());
        }
    };

    const handleResourceClick = (id: string, type: 'dataSource' | 'index' | 'rag') => {
        if (selectedId === id) {
            setSelectedId(null);
            setConnectedIds(new Set());
            return;
        }

        setSelectedId(id);
        const newConnectedIds = new Set<string>();
        newConnectedIds.add(id);

        if (type === 'dataSource') {
            data.indexes.forEach(index => {
                if (index.dataSourceIds.includes(id)) {
                    newConnectedIds.add(index.id);
                    data.ragApplications.forEach(rag => {
                        if (rag.indexIds.includes(index.id)) {
                            newConnectedIds.add(rag.id);
                        }
                    });
                }
            });
        } else if (type === 'index') {
            const index = data.indexes.find(i => i.id === id);
            if (index) {
                index.dataSourceIds.forEach(dsId => {
                    newConnectedIds.add(dsId);
                });
                data.ragApplications.forEach(rag => {
                    if (rag.indexIds.includes(id)) {
                        newConnectedIds.add(rag.id);
                    }
                });
            }
        } else if (type === 'rag') {
            const rag = data.ragApplications.find(r => r.id === id);
            if (rag) {
                rag.indexIds.forEach(indexId => {
                    newConnectedIds.add(indexId);
                    const index = data.indexes.find(i => i.id === indexId);
                    if (index) {
                        index.dataSourceIds.forEach(dsId => {
                            newConnectedIds.add(dsId);
                        });
                    }
                });
            }
        }

        setConnectedIds(newConnectedIds);
    };

    const renderResourceBox = (
        title: string,
        icon: React.ReactNode,
        items: Resource[],
        boxRef: React.MutableRefObject<HTMLDivElement | null>,
        prefix: string,
        itemColors?: Record<string, string>
    ) => (
        <Card
            className="h-100"
            ref={boxRef}
            style={{ minWidth: '250px', flex: 1 }}
        >
            <Card.Body>
                <div className="d-flex align-items-center mb-3">
                    {icon}
                    <h5 className="card-title mb-0 ms-2">{title}</h5>
                    <span className="ms-2 text-muted">({items.length})</span>
                </div>
                {items.length === 0 ? (
                    <div className="text-muted text-center p-3">
                        No {title.toLowerCase()} available
                    </div>
                ) : (
                    items.map(item => {
                        const isHighlighted = !selectedId || connectedIds.has(item.id);
                        const isSelected = selectedId === item.id;
                        const displayName = item.file_path || item.metadata?.display_path || item.path || item.name;

                        return (
                            <div
                                id={`${prefix}-${item.id}`}
                                key={item.id}
                                className="p-2 rounded mb-2 resource-item"
                                style={{
                                    borderLeft: itemColors?.[item.id] ? `4px solid ${itemColors[item.id]}` : 'none',
                                    border: isSelected ? '2px solid #3B82F6' : '1px solid rgba(0,0,0,0.05)',
                                    boxShadow: isHighlighted ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                    cursor: 'pointer',
                                    background: isSelected ? '#ececec' : (isHighlighted ? '#ececec' : '#ffffff'),
                                    opacity: isHighlighted || isSelected ? 1 : 0.5,
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleResourceClick(
                                        item.id,
                                        prefix === 'doc' ? 'dataSource' :
                                            prefix === 'index' ? 'index' : 'rag'
                                    );
                                }}
                            >
                                <div className="d-flex gap-2">
                                    <div className="flex-shrink-0 d-flex" style={{marginTop: '2px'}}>
                                        {prefix === 'doc' && getSourceTypeIcon(item.source_type)}
                                        {prefix === 'index' && getKnowledgeBaseTypeIcon(item as Index)}
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                        <div className="fw-medium small">{displayName}</div>
                                        {item.description && (
                                            <div
                                                className="text-muted small"
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordWrap: 'break-word',
                                                    wordBreak: 'break-word',
                                                    marginTop: '2px'
                                                }}
                                            >
                                                {item.description}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </Card.Body>
        </Card>
    );

    const calculateArrowhead = (start: Position, end: Position) => {
        const arrowLength = 10;
        const arrowWidth = 6;
        const angle = Math.atan2(end.y - start.y, end.x - start.x);

        const x1 = end.x - arrowLength * Math.cos(angle) + arrowWidth * Math.sin(angle);
        const y1 = end.y - arrowLength * Math.sin(angle) - arrowWidth * Math.cos(angle);
        const x2 = end.x - arrowLength * Math.cos(angle) - arrowWidth * Math.sin(angle);
        const y2 = end.y - arrowLength * Math.sin(angle) + arrowWidth * Math.cos(angle);

        return `${end.x},${end.y} ${x1},${y1} ${x2},${y2}`;
    };

    useEffect(() => {
        const calculateArrows = () => {
            if (!containerRef.current) return;

            const newArrows: Arrow[] = [];
            const containerRect = containerRef.current.getBoundingClientRect();

            data.indexes.forEach((index, indexNum) => {
                const indexEl = document.getElementById(`index-${index.id}`);
                const color = CONNECTION_COLORS[indexNum % CONNECTION_COLORS.length];

                if (!indexEl) {
                    console.warn(`Index element not found: ${index.id}`);
                    return;
                }

                index.dataSourceIds.forEach(docId => {
                    const docEl = document.getElementById(`doc-${docId}`);

                    if (docEl) {
                        const docRect = docEl.getBoundingClientRect();
                        const indexRect = indexEl.getBoundingClientRect();

                        const isHighlighted = !selectedId ||
                            (connectedIds.has(docId) && connectedIds.has(index.id));

                        // Calculate center points
                        const startX = docRect.right - containerRect.left;
                        const startY = docRect.top - containerRect.top + (docRect.height / 2);
                        const endX = indexRect.left - containerRect.left;
                        const endY = indexRect.top - containerRect.top + (indexRect.height / 2);

                        newArrows.push({
                            start: { x: startX, y: startY },
                            end: { x: endX, y: endY },
                            id: `doc-${docId}-${index.id}`,
                            color,
                            isHighlighted
                        });
                    }
                });
            });

            data.ragApplications.forEach((rag) => {
                const ragEl = document.getElementById(`rag-${rag.id}`);

                if (!ragEl) {
                    console.warn(`RAG element not found: ${rag.id}`);
                    return;
                }

                rag.indexIds.forEach(indexId => {
                    const indexEl = document.getElementById(`index-${indexId}`);
                    if (!indexEl) {
                        console.warn(`Index element not found: ${indexId}`);
                        return;
                    }

                    const indexNum = data.indexes.findIndex(i => i.id === indexId);
                    const color = CONNECTION_COLORS[indexNum % CONNECTION_COLORS.length];

                    const indexRect = indexEl.getBoundingClientRect();
                    const ragRect = ragEl.getBoundingClientRect();

                    const isHighlighted = !selectedId ||
                        (connectedIds.has(rag.id) && connectedIds.has(indexId));

                    // Calculate center points
                    const startX = indexRect.right - containerRect.left;
                    const startY = indexRect.top - containerRect.top + (indexRect.height / 2);
                    const endX = ragRect.left - containerRect.left;
                    const endY = ragRect.top - containerRect.top + (ragRect.height / 2);

                    newArrows.push({
                        start: { x: startX, y: startY },
                        end: { x: endX, y: endY },
                        id: `index-${indexId}-${rag.id}`,
                        color,
                        isHighlighted
                    });
                });
            });

            setArrows(newArrows);
        };

        calculateArrows();
        window.addEventListener('resize', calculateArrows);
        return () => window.removeEventListener('resize', calculateArrows);
    }, [data, selectedId, connectedIds]);

    const { indexColors, ragColors } = useMemo(() => ({
        indexColors: data.indexes.reduce((acc, index, i) => {
            acc[index.id] = CONNECTION_COLORS[i % CONNECTION_COLORS.length];
            return acc;
        }, {} as Record<string, string>),
        ragColors: data.ragApplications.reduce((acc, rag, i) => {
            acc[rag.id] = CONNECTION_COLORS[(i + CONNECTION_COLORS.length/2) % CONNECTION_COLORS.length];
            return acc;
        }, {} as Record<string, string>)
    }), [data.indexes, data.ragApplications]);

    return (
        <Card>
            <Card.Body onClick={handleOutsideClick}>
                <div className="position-relative" ref={containerRef} style={{ minHeight: '300px' }}>
                    <Row className="g-4">
                        <Col>
                            {renderResourceBox(
                                "Data Sources",
                                <Database className="text-secondary" size={24} />,
                                filteredDataSources,
                                docsRef,
                                "doc"
                            )}
                        </Col>
                        <Col>
                            {renderResourceBox(
                                "Knowledge Bases",
                                <Library className="text-purple" size={24} />,
                                data.indexes,
                                indexesRef,
                                "index",
                                indexColors
                            )}
                        </Col>
                        <Col>
                            {renderResourceBox(
                                "RAG Applications",
                                <Brain className="text-success" size={24} />,
                                data.ragApplications,
                                ragsRef,
                                "rag",
                                ragColors
                            )}
                        </Col>
                    </Row>
                    <svg
                        className="position-absolute"
                        style={{ top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                    >
                        {arrows.map(({ start, end, id, color, isHighlighted }) => (
                            <g key={id}>
                                <line
                                    x1={start.x}
                                    y1={start.y}
                                    x2={end.x}
                                    y2={end.y}
                                    stroke={color}
                                    strokeWidth="1.5"
                                    opacity={isHighlighted ? 0.8 : 0.2}
                                />
                                <polygon
                                    points={calculateArrowhead(start, end)}
                                    fill={color}
                                    opacity={isHighlighted ? 0.8 : 0.2}
                                />
                            </g>
                        ))}
                    </svg>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ResourceRelationshipDiagram;